import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { EntityWithDocumentFullResponse } from '@/modules/entity/types/EntityItemResponse.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { CertificateFiltersPanel } from '@/modules/issuer-dashboard/components/certificates/CertificateFiltersPanel.tsx';
import { Filters } from '@/modules/issuer-dashboard/components/certificates/types.ts';
import { IssuerAddAnnouncementModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddAnnouncementModal.tsx';
import { useIssuerGetCertificatesQuery } from '@/modules/issuer-dashboard/queries/certificates.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  Badge,
  BodyCell,
  EmptyListState,
  ModalProviderContext,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  TooltipNew,
} from 'ui';
import { useIsMobile, useSaveState } from 'ui/hooks';
import { ArchiveBlackIcon, DownloadIcon, SendGreyIcon } from 'ui/icons';
import { goodFormatNumber, goodFormatUSDCurrency } from 'ui/utils';
import { ShareCertificateResponse, ShareCertificateStatus } from 'api';

interface CertificateBodyProps {
  hideChooseRegisterClass?: boolean;
  register_class_id?: number;
}

export const CertificateBody = ({
  hideChooseRegisterClass,
  register_class_id,
}: CertificateBodyProps) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isMobile } = useIsMobile();
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const { openModal } = useContext(ModalProviderContext);
  const [filters, setFilters] = useSaveState<Filters>(
    {
      register_class_id,
    },
    `filters-certificate-${registerId}`,
  );

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useIssuerGetCertificatesQuery(
    {
      page: 1,
      page_size: 30,
      status: filters.status,
      from_date: filters.from_date,
      to_date: filters.to_date,
      register_class_id: register_class_id ?? filters.register_class_id,
      shareholder_id: filters.shareholder_id,
    },
  );
  const certificates = (data?.pages || []).flatMap(({ data }) => data);

  const headItems = useMemo<TableHeadColumn[]>(() => {
    let items: TableHeadColumn[] = [
      { title: 'Investor' },
      { title: 'Class' },
      { title: 'Issue price' },
      { title: 'Shares/units' },
      { title: 'Total value' },
      { title: 'Date' },
      { title: 'Status' },
      { title: '', className: 'min-w-12' },
    ];

    if (hideChooseRegisterClass) {
      items = items.filter(item => item.title !== 'Class');
    }

    return items;
  }, [hideChooseRegisterClass]);

  const handleOpenCommunicationModal = useCallback(
    (item: ShareCertificateResponse) => () => {
      const { entity, register_data, register_class } = item;
      openModal(IssuerAddAnnouncementModal, {
        certificates: [
          {
            label: (
              <div className="flex items-center gap-2 text-sm leading-[22px] font-[Inter]">
                <div className="max-w-[130px] overflow-hidden whitespace-nowrap text-ellipsis text-gray-900 sm:max-w-[200px] 2xl:max-w-[350px]">
                  {entity?.entity_name || register_data?.name || '---'}
                </div>
              </div>
            ),
            value: item,
          },
        ],
        registerClass: register_class
          ? {
              value: register_class.id,
              label: register_class.class_name,
            }
          : undefined,
      });
    },
    [openModal],
  );

  const handleGoToHolderPage = useCallback(
    (entityId: number) => {
      navigate(
        {
          pathname: issuerDashboardPaths.dynamic.holder(entityId.toString()),
          search: params.toString(),
        },
        {
          state: { from: pathname + '?' + params.toString() },
        },
      );
    },
    [navigate, params, pathname],
  );

  const items = useMemo<TableBodyColumn[]>(
    () =>
      certificates.map(certificate => {
        const {
          status,
          is_archived,
          archived_at,
          created_at,
          register_class,
          price_per_share,
          quantity_of_shares,
          file_storage_item_url,
        } = certificate;

        const entity = certificate.entity as unknown as EntityWithDocumentFullResponse;

        const entityName =
          ('business_name_trustee' in (entity?.smsf_entity_details || {}) &&
            !!entity?.smsf_entity_details?.business_name_trustee) ||
          ('business_name_trustee' in (entity?.trust_entity_details || {}) &&
            !!entity?.trust_entity_details?.business_name_trustee)
            ? `${
                entity?.trust_entity_details?.business_name_trustee ||
                entity?.smsf_entity_details?.business_name_trustee
              } <${entity?.entity_name}>`
            : `${entity?.entity_name}`;

        let cells: BodyCell[] = [
          {
            cell: (
              <span
                className="cursor-pointer font-medium text-blue-700 hover:underline"
                onClick={() => handleGoToHolderPage(entity.id)}
              >
                {entityName ?? '---'}
              </span>
            ),
          },
          { cell: register_class?.class_name || '-' },
          { cell: goodFormatUSDCurrency(price_per_share) },
          { cell: goodFormatNumber(quantity_of_shares) },
          { cell: goodFormatUSDCurrency(price_per_share * quantity_of_shares) },
          { cell: <>{dateToPrint(created_at, DateVariant.Table, true) || '-'}</> },
          {
            cell: (
              <Badge
                className={classNames('w-fit capitalize text-white', {
                  'bg-green-600': status === ShareCertificateStatus.Current,
                  'bg-gray-400': status === ShareCertificateStatus.Outdated,
                })}
              >
                {status}
              </Badge>
            ),
          },
          {
            cell: (
              <div className="flex select-none items-center justify-end gap-2 sm:gap-4 [&_img]:!h-[18px] [&_img]:!w-[18px] [&_img]:!min-h-[18px] [&_img]:!min-w-[18px] sm:[&_img]:!h-6 sm:[&_img]:!w-6 sm:[&_img]:!min-h-6 sm:[&_img]:!min-w-6">
                {is_archived && (
                  <TooltipNew
                    text={`This certificate belongs to an archived transaction. It was archived at ${dateToPrint(
                      archived_at,
                      DateVariant.Information,
                      true,
                    )}`}
                  >
                    <img
                      src={ArchiveBlackIcon}
                      alt="Archive"
                      className="h-6 w-6 min-h-[24px] min-w-[24px]"
                    />
                  </TooltipNew>
                )}
                {status !== ShareCertificateStatus.Outdated && (
                  <button onClick={handleOpenCommunicationModal(certificate)}>
                    <img
                      src={SendGreyIcon}
                      alt="send"
                      className="h-6 w-6 min-h-[24px] min-w-[24px] cursor-pointer hover:opacity-80"
                    />
                  </button>
                )}
                {file_storage_item_url && (
                  <div className="flex flex-wrap items-center justify-center gap-2 sm:flex-nowrap sm:justify-end">
                    <span
                      className="cursor-pointer hover:opacity-80"
                      onClick={e => e.stopPropagation()}
                    >
                      <a href={file_storage_item_url} target="_blank" rel="noopener noreferrer">
                        <img
                          alt="cerificate"
                          src={DownloadIcon}
                          className="h-6 w-6 min-h-[24px] min-w-[24px]"
                        />
                      </a>
                    </span>
                  </div>
                )}
              </div>
            ),
          },
        ];

        if (hideChooseRegisterClass) {
          cells = cells.filter((_, i) => i !== 1);
        }

        return {
          cells,
        };
      }),
    [certificates, handleGoToHolderPage, handleOpenCommunicationModal, hideChooseRegisterClass],
  );

  return (
    <>
      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full items-start justify-between gap-4 px-3 sm:px-6">
          <CertificateFiltersPanel
            setFilters={setFilters}
            filters={filters}
            isFetching={isFetching}
            hideChooseRegisterClass={hideChooseRegisterClass}
          />
        </div>
        {(!!items.length || isLoading) && (
          <Table
            classNameHead="border-t-0"
            withRounded={isMobile}
            withBorder={true}
            withBorderX={false}
            isLoading={isLoading}
            fetchNextPageInfinite={fetchNextPage}
            hasNextPageInfinite={hasNextPage}
            headItems={headItems}
            items={items}
            isSearchUsed={!isEmpty(filters)}
            blockIdInfinite="scrollable"
          />
        )}
        {!items.length && (
          <div className="flex h-[60dvh] w-full items-center justify-between gap-4 px-3 sm:px-6">
            <EmptyListState
              title="No certificates yet"
              description="It seems there are no certificates to display at the moment. Certificates are automatically generated after balance updates or transactions are completed."
              isLoading={isFetching}
              isSearchUsed={!isEmpty(filters)}
              itemsCount={items.length}
            />
          </div>
        )}
      </div>
    </>
  );
};
